import axios from 'axios'
import store from '@/store'
import { Message } from 'element-ui'
import { getAccessToken, setAccessToken } from '@/utils/localStorage'

const service = axios.create({
  baseURL: window.location.origin,
  timeout: 300000, // 5 分鐘
  withCredentials: true
})

const refreshService = axios.create({
  baseURL: window.location.origin,
  timeout: 10000, // 10 秒
  withCredentials: true
})

// 全域 Flag
let isRefreshingToken = false
let isLoggingOut = false

/**
 * @example
 * [{ resolve: (token) => {}, reject: (error) => {} }]
 */
let pendingRequests = []

/**
 * 當 Token 刷新完成後，處理所有等待中的 Promise
 * @param {string|null} token - 新的 Access Token
 * @param {Error|null} error - 如果刷新失敗，傳入錯誤物件
 */
function processQueue(token, error) {
  pendingRequests.forEach((promiseObj) => {
    if (token) promiseObj.resolve(token)
    else promiseObj.reject(error)
  })
  pendingRequests = []
}

/**
 * 強制登出
 */
function forceLogout() {
  if (isLoggingOut) return
  isLoggingOut = true

  store.dispatch('user/logout').then(() => {
    sessionStorage.setItem('showReLoginMessage', 'true')
    location.reload()
  })
}

/**
 * 顯示錯誤訊息
 * @param {Error} error - 錯誤物件
 */
function handleShowErrorMsg(error) {
  let errorMessage = '未知錯誤'

  if (error) {
    if (error.response && error.response.data && error.response.data.message) {
      errorMessage = error.response.data.message
    } else if (error.message) {
      errorMessage = error.message
    }
  }

  Message({
    message: errorMessage,
    type: 'error',
    duration: 4000
  })

  return Promise.reject(error)
}

/**
 * 設定 Authorization header
 */
function setAuthHeader(config) {
  const token = getAccessToken()
  if (token) {
    config.headers.authorization = `Bearer ${token}`
  }
  return config
}

/**
 * 處理成功響應
 */
function handleResponse(response) {
  const data = response.data

  // 檔案下載需保留完整 response
  if (data instanceof Blob) return response

  // 業務邏輯失敗
  if (!data.success) {
    return Promise.reject(new Error(data.message))
  }

  return data
}

/**
 * 處理錯誤響應
 */
function handleResponseError(error) {
  const reqInfo = error.config

  // 處理 401 (Access Token 過期)
  if (!reqInfo._retry && error.response && error.response.status === 401) {
    reqInfo._retry = true // 避免無限重試

    // 如果正在刷新 Access Token，則將請求加入等待隊列，等待刷新完成後重試
    if (isRefreshingToken) {
      return new Promise((resolve, reject) => {
        pendingRequests.push({ resolve, reject })
      })
        .then((newToken) => {
          reqInfo.headers.authorization = `Bearer ${newToken}`
          return service(reqInfo)
        })
        .catch((err) => Promise.reject(err))
    }

    isRefreshingToken = true

    return refreshService
      .post('/api/user/refresh', { tenantId: store.getters.TENANT_ID, clientType: 'admin' })
      .then((res) => {
        const newAccessToken = res.data.data.accessToken
        setAccessToken(newAccessToken)
        processQueue(newAccessToken, null)

        reqInfo.headers.authorization = `Bearer ${newAccessToken}`
        return service(reqInfo)
      })
      .catch((err) => {
        processQueue(null, err)
        forceLogout()
        return Promise.resolve()
      })
      .finally(() => {
        isRefreshingToken = false
      })
  }

  return handleShowErrorMsg(error)
}

service.interceptors.request.use(setAuthHeader, handleShowErrorMsg)
service.interceptors.response.use(handleResponse, handleResponseError)

export default service
